import ExcelJS from 'exceljs';
import type { EmployeeTimesheet, DayData } from './types';
import { calculateSummary, isSunday } from './summary';

/**
 * Helper function để tạo tên file Excel
 * Hỗ trợ tiếng Việt có dấu
 */
const generateFileName = (
  month: number,
  year: number,
  departmentName?: string | null,
  employeeName?: string | null,
  employeeCode?: string | null
): string => {
  const monthStr = month.toString().padStart(2, '0');
  const yearStr = year.toString();

  // Nếu có lọc theo nhân sự cụ thể
  if (employeeName && employeeCode) {
    // Giữ nguyên tên tiếng Việt có dấu
    const sanitizedEmployeeName = employeeName.trim();
    const sanitizedEmployeeCode = employeeCode.trim();
    return `${sanitizedEmployeeName}_${sanitizedEmployeeCode}_${monthStr}_${yearStr}.xlsx`;
  }

  // Nếu có lọc theo phòng ban
  if (departmentName && departmentName !== 'Tất cả') {
    // Giữ nguyên tên phòng ban tiếng Việt có dấu
    const sanitizedDepartmentName = departmentName.trim();
    return `${sanitizedDepartmentName}_${monthStr}_${yearStr}.xlsx`;
  }

  // Mặc định: Bang_Cham_Cong_Thang_Nam
  return `Bang_Cham_Cong_${monthStr}_${yearStr}.xlsx`;
};

/**
 * Helper function để tạo style cho header
 */
const getHeaderStyle = () => ({
  fill: {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'FF1F4E78' },
  },
  font: {
    bold: true,
    color: { argb: 'FFFFFFFF' },
    size: 11,
  },
  alignment: {
    vertical: 'middle' as const,
    horizontal: 'center' as const,
    wrapText: true,
  },
  border: {
    top: { style: 'thin' as const, color: { argb: 'FF000000' } },
    left: { style: 'thin' as const, color: { argb: 'FF000000' } },
    bottom: { style: 'thin' as const, color: { argb: 'FF000000' } },
    right: { style: 'thin' as const, color: { argb: 'FF000000' } },
  },
});

/**
 * Helper function để tạo style cho cell dữ liệu
 */
const getDataCellStyle = (isEvenRow: boolean = false) => ({
  fill: {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: isEvenRow ? 'FFF2F2F2' : 'FFFFFFFF' },
  },
  font: {
    size: 10,
    color: { argb: 'FF000000' },
  },
  border: {
    top: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
    left: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
    right: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
  },
  alignment: {
    vertical: 'middle' as const,
    horizontal: 'left' as const,
    wrapText: true,
  },
});

/**
 * Xuất bảng chấm công ra file Excel
 */
export async function exportAttendanceToExcel(
  employees: EmployeeTimesheet[],
  allDays: DayData[],
  month: number,
  year: number,
  options?: {
    departmentName?: string | null;
    employeeName?: string | null;
    employeeCode?: string | null;
  }
): Promise<void> {
  if (!employees || employees.length === 0) {
    throw new Error('Không có dữ liệu để xuất file');
  }

  // Lọc bỏ các employee không hợp lệ (không có tên hoặc mã)
  const validEmployees = employees.filter(
    (emp) => emp && (emp.employeeName?.toString().trim() || emp.employeeCode?.toString().trim())
  );

  if (validEmployees.length === 0) {
    throw new Error('Không có dữ liệu hợp lệ để xuất file');
  }

  // Tạo tên file
  const fileName = generateFileName(
    month,
    year,
    options?.departmentName,
    options?.employeeName,
    options?.employeeCode
  );

  // Tạo workbook mới
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Bảng Chấm Công');

  const headers = [
    'STT', 'Mã NV', 'Họ và tên', 'Tổng', 'Tăng ca (h)', 'Vi phạm (phút)',
    'Nghỉ', 'Phụ cấp', 'Tổng thực tế',
    ...allDays.map((day) => `Ngày ${day.dayNumber}`),
  ];
  const dayColumnMap = new Map<number, boolean>();
  allDays.forEach((day, index) => dayColumnMap.set(index + 10, isSunday(day.date)));

  const headerRow = worksheet.addRow(headers);
  headerRow.eachCell((cell, colNumber) => {
    Object.assign(cell, getHeaderStyle());
    if (dayColumnMap.get(colNumber)) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } };
    }
  });
  headerRow.height = 30;

  validEmployees.forEach((emp, index) => {
    const summary = calculateSummary(emp);
    const rowData: (string | number)[] = [
      index + 1,
      emp.employeeCode,
      emp.employeeName,
      parseFloat(summary.total),
      parseFloat(summary.overtime.toString()),
      parseInt(summary.violation.toString()),
      parseFloat(summary.leave),
      parseInt(summary.mealAllowance.toString()),
      parseFloat(summary.actualTotal),
    ];

    allDays.forEach((day) => {
      const dayData = emp.days.find((entry) => entry.dayNumber === day.dayNumber);
      let value = '';
      if (!isSunday(day.date) && dayData) {
        if (dayData.status === 'absent') value = 'V';
        else if (dayData.status === 'leave') value = 'P';
        else if (dayData.status === 'unpaidLeave') value = 'P (KP)';
        else if (dayData.checkIn || dayData.checkOut) {
          value = `Vào: ${dayData.checkIn || '—'}\nRa: ${dayData.checkOut || '—'}`;
        } else if (dayData.status === 'pending') value = '?';
      }
      rowData.push(value);
    });

    const row = worksheet.addRow(rowData);
    const isEvenRow = index % 2 === 0;
    row.eachCell((cell, colNumber) => {
      Object.assign(cell, getDataCellStyle(isEvenRow));
      cell.alignment = {
        vertical: 'middle',
        horizontal: colNumber === 3 ? 'left' : 'center',
        wrapText: true,
      };
      if (colNumber >= 4 && colNumber <= 9 && typeof cell.value === 'number') {
        cell.numFmt = colNumber === 5 || colNumber === 6 || colNumber === 8 ? '0' : '0.0';
      }
      if (dayColumnMap.get(colNumber)) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: isEvenRow ? 'FFE8E8E8' : 'FFF5F5F5' } };
      }
    });
    row.height = emp.days.some((day) => day.checkIn || day.checkOut) ? 40 : 25;
  });

  [8, 12, 25, 10, 12, 15, 10, 12, 15].forEach((width, index) => {
    worksheet.getColumn(index + 1).width = width;
  });
  allDays.forEach((_day, index) => { worksheet.getColumn(index + 10).width = 12; });

  worksheet.views = [{
    state: 'frozen',
    xSplit: 9,
    ySplit: 1,
    topLeftCell: 'J2',
    activeCell: 'J2',
  }];

  // Tạo buffer và download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
